import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { firestore } from "../../../firebase";
import { getScheduleDueStats } from "../../../utils/amcScheduleStats";
// --- MUI Imports ---
import {
  Paper,
  Typography,
  IconButton,
  Box,
  Grid,
  Card,
} from "@mui/material";
// --- End MUI Imports ---
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import OrganisationAdminNavBar from "./OrganisationAdminNavBar";

const OUOperatorPage = () => {
  const navigate = useNavigate();

  // State for stats
  const [shopStats, setShopStats] = useState({
    totalShops: 0,
    activeShops: 0,
    totalComplaints: 0,
  });
  const [amcCount, setAmcCount] = useState(0);
  const [amcDueNextMonth, setAmcDueNextMonth] = useState(0);
  const [amcDueToday, setAmcDueToday] = useState(0);

  const auth = getAuth();

  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const endOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);
  const oneMonthFromNow = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Fetch user profile to get tenantId
          const qUser = query(
            collection(firestore, "UserProfileMaster"),
            where("email", "==", user.email)
          );
          const userSnap = await getDocs(qUser);

          if (!userSnap.empty) {
            const profileData = userSnap.docs[0].data();
            const tenantId = profileData.tenantId;
            const role = profileData.role;
            sessionStorage.setItem("userRole", role);

            // 1. Fetch Shops (Filter by tenantId if possible, or global for now as per seller)
            // If Shops have tenantId, we should filter. For now, matching Seller's global count.
            const unsubShops = onSnapshot(collection(firestore, "Shops"), (snap) => {
              setShopStats((prev) => ({
                ...prev,
                totalShops: snap.size,
                activeShops: snap.docs.filter((d) => d.data().status === "Active").length,
              }));
            });

            // 2. Fetch Complaints (Filter by tenantId if available, or role-based)
            const unsubComplaints = onSnapshot(collection(firestore, "Complaints"), (snap) => {
              // OU Operator should only see complaints for their institution (tenantId)
              const userComplaints = snap.docs.filter(d => {
                const data = d.data();
                // Filter by tenantId if it exists on the complaint, otherwise fallback
                return data.tenantId === tenantId || data.userId === user.uid;
              });
              setShopStats((prev) => ({ ...prev, totalComplaints: userComplaints.length }));
            });

            // 3. Fetch AMC stats (Filter by tenantId)
            const unsubAmc = onSnapshot(collection(firestore, "AMCScheduleMaster"), (snap) => {
              const schedules = snap.docs
                .map((entry) => ({ id: entry.id, ...entry.data() }))
                .filter((schedule) => schedule.tenantId === tenantId);
              setAmcCount(schedules.length);
              const { dueToday, dueNextMonth } = getScheduleDueStats(
                schedules,
                startOfToday,
                endOfToday,
                oneMonthFromNow
              );
              setAmcDueNextMonth(dueNextMonth);
              setAmcDueToday(dueToday);
            });

            return () => {
              unsubShops();
              unsubComplaints();
              unsubAmc();
            };
          }
        } catch (error) {
          console.error("Error loading OU Operator dashboard data:", error);
        }
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribeAuth();
  }, [auth, navigate, startOfToday, endOfToday, oneMonthFromNow]);

  const Odometer = ({ label, value, color }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        backgroundColor: "#fafafa",
        borderRadius: "8px",
        height: "120px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary", m: 0, fontSize: "0.875rem" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, m: 0, color: color, mt: 1 }}>
        {value}
      </Typography>
    </Paper>
  );

  const ActionCard = ({ title, text, navigateTo }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "12px",
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {text}
        </Typography>
      </Box>
      <IconButton color="primary" onClick={() => navigate(navigateTo)}>
        <ArrowForwardIosIcon />
      </IconButton>
    </Paper>
  );

  return (
    <Box sx={{ bgcolor: "#f5f7fb", minHeight: "100vh" }}>
      <OrganisationAdminNavBar />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          OU Operator
        </Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Seller & Complaint Management
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Total Shops" value={shopStats.totalShops} color="#1976d2" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Active Shops" value={shopStats.activeShops} color="#388E3C" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Total Complaints" value={shopStats.totalComplaints} color="#D32F2F" />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                <ActionCard title="Manage Business Vendor" text="Create or update your business vendor records." navigateTo="/addshop" />
                <ActionCard title="Complaints" text="View complaints related to your business vendors." navigateTo="/complaints_list" />
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  AMC
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Total available" value={amcCount} color="#1976d2" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Due in next month" value={amcDueNextMonth} color="#EF6C00" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Targeted for today" value={amcDueToday} color="#D32F2F" />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                <ActionCard title="Create New AMC" text="Set up a new Annual Maintenance Contract." navigateTo="/amc/schedule/new" />
                <ActionCard title="Manage AMC Schedules" text="View, update, or track AMC schedules." navigateTo="/amc/schedule/list" />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OUOperatorPage;
